import {
  ArrowSquareOut,
  ArrowsDownUp,
  BookmarkSimple,
  Code,
  FunnelSimple,
  Star,
} from "@phosphor-icons/react";
import { formatCapeTownDate } from "@shared/lib/dates";
import type { GithubShowcaseCategory, GithubShowcaseEntry } from "@shared/schemas/domain";
import { useEffect, useMemo, useState } from "react";
import { CustomSelect, type CustomSelectOption } from "@/components/CustomSelect";
import { readStorage, STORAGE_KEYS, writeStorage } from "@/lib/storage";

const CATEGORY_OPTIONS = ["all", "agent-systems", "browser-control", "context-protocols", "model-systems"] as const;
const STAR_OPTIONS = ["all", "rising", "growing", "established"] as const;
const TECHNOLOGY_OPTIONS = ["all", "Python", "TypeScript"] as const;
const SORT_OPTIONS = ["relevance", "stars-desc", "stars-asc", "recent"] as const;

type CategoryFilter = (typeof CATEGORY_OPTIONS)[number];
type StarFilter = (typeof STAR_OPTIONS)[number];
type TechnologyFilter = (typeof TECHNOLOGY_OPTIONS)[number];
type SortFilter = (typeof SORT_OPTIONS)[number];

interface ShowcaseFilters {
  category: CategoryFilter;
  stars: StarFilter;
  technology: TechnologyFilter;
  sort: SortFilter;
}

const DEFAULT_FILTERS: ShowcaseFilters = {
  category: "all",
  stars: "all",
  technology: "all",
  sort: "relevance",
};

const CATEGORY_LABELS: Record<GithubShowcaseCategory, string> = {
  "agent-systems": "Agent systems",
  "browser-control": "Browser control",
  "context-protocols": "Context + protocols",
  "model-systems": "Model systems",
};

const CATEGORY_FILTER_OPTIONS: readonly CustomSelectOption<CategoryFilter>[] = [
  { value: "all", label: "All fields" },
  { value: "agent-systems", label: "Agent systems" },
  { value: "browser-control", label: "Browser control" },
  { value: "context-protocols", label: "Context + protocols" },
  { value: "model-systems", label: "Model systems" },
];

const STAR_FILTER_OPTIONS: readonly CustomSelectOption<StarFilter>[] = [
  { value: "all", label: "Any star range" },
  { value: "rising", label: "Rising · under 10k" },
  { value: "growing", label: "Growing · 10–50k" },
  { value: "established", label: "Established · 50k+" },
];

const TECHNOLOGY_FILTER_OPTIONS: readonly CustomSelectOption<TechnologyFilter>[] = [
  { value: "all", label: "All tech" },
  { value: "Python", label: "Python" },
  { value: "TypeScript", label: "TypeScript" },
];

const SORT_FILTER_OPTIONS: readonly CustomSelectOption<SortFilter>[] = [
  { value: "relevance", label: "Most relevant" },
  { value: "stars-desc", label: "Most starred" },
  { value: "stars-asc", label: "Fewest stars" },
  { value: "recent", label: "Recently active" },
];

function isOption<const Options extends readonly string[]>(options: Options, value: unknown): value is Options[number] {
  return typeof value === "string" && options.includes(value);
}

function storedFilters(): ShowcaseFilters {
  const value = readStorage<unknown>(STORAGE_KEYS.githubShowcaseFilters, DEFAULT_FILTERS);
  if (!value || typeof value !== "object") return DEFAULT_FILTERS;
  const stored = value as Partial<ShowcaseFilters>;
  return {
    category: isOption(CATEGORY_OPTIONS, stored.category) ? stored.category : DEFAULT_FILTERS.category,
    stars: isOption(STAR_OPTIONS, stored.stars) ? stored.stars : DEFAULT_FILTERS.stars,
    technology: isOption(TECHNOLOGY_OPTIONS, stored.technology) ? stored.technology : DEFAULT_FILTERS.technology,
    sort: isOption(SORT_OPTIONS, stored.sort) ? stored.sort : DEFAULT_FILTERS.sort,
  };
}

function matchesStarBand(stars: number | null, filter: StarFilter): boolean {
  if (filter === "all") return true;
  if (stars === null) return false;
  if (filter === "rising") return stars < 10_000;
  if (filter === "growing") return stars >= 10_000 && stars < 50_000;
  return stars >= 50_000;
}

function compactNumber(value: number | null): string {
  if (value === null) return "Sync pending";
  return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

function GithubProject({ entry }: { entry: GithubShowcaseEntry }) {
  return (
    <a
      className="github-project external-link external-link--block"
      data-spotlight={entry.spotlight ? "true" : "false"}
      href={entry.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="github-project__heading">
        <span className="github-project__rank">{entry.rank}.</span>
        <strong>{entry.name}</strong>
        <ArrowSquareOut aria-hidden="true" />
      </span>
      <span className="github-project__why">
        <small>Why it matters</small>
        <span>{entry.whyItMatters}</span>
      </span>
      <span className="github-project__tags">
        <span>{CATEGORY_LABELS[entry.category]}</span>
        <span>{entry.technology}</span>
      </span>
      <span className="github-project__meta">
        <span><Star aria-hidden="true" /> {compactNumber(entry.stars)}{entry.stars === null ? "" : " stars"}</span>
        <span>{entry.pushedAt ? `Pushed ${formatCapeTownDate(entry.pushedAt)}` : "Activity awaiting sync"}</span>
      </span>
    </a>
  );
}

export function GithubShowcase({ entries }: { entries: GithubShowcaseEntry[] }) {
  const [filters, setFilters] = useState<ShowcaseFilters>(storedFilters);

  useEffect(() => {
    writeStorage(STORAGE_KEYS.githubShowcaseFilters, filters);
  }, [filters]);

  const visibleEntries = useMemo(() => {
    const filtered = entries.filter((entry) => (
      (filters.category === "all" || entry.category === filters.category)
      && (filters.technology === "all" || entry.technology === filters.technology)
      && matchesStarBand(entry.stars, filters.stars)
    ));
    return filtered.sort((left, right) => {
      if (filters.sort === "stars-desc") return (right.stars ?? -1) - (left.stars ?? -1);
      if (filters.sort === "stars-asc") return (left.stars ?? Number.POSITIVE_INFINITY) - (right.stars ?? Number.POSITIVE_INFINITY);
      if (filters.sort === "recent") return (right.pushedAt ?? "").localeCompare(left.pushedAt ?? "");
      return left.rank - right.rank;
    });
  }, [entries, filters]);

  const latestFetch = entries
    .map((entry) => entry.fetchedAt)
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1);

  const updateFilter = <Key extends keyof ShowcaseFilters>(key: Key, value: ShowcaseFilters[Key]): void => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  return (
    <section className="github-showcase" aria-labelledby="github-showcase-heading">
      <div className="github-showcase__intro">
        <div>
          <p className="github-showcase__label">GitHub field notes</p>
          <h2 id="github-showcase-heading">Nine repositories worth opening.</h2>
          <p>Fixed curation. Live stars and maintenance—without refresh roulette.</p>
        </div>
        <div className="github-showcase__status">
          <span><strong>{entries.length}</strong> saved</span>
          <span><BookmarkSimple aria-hidden="true" /> Fixed set</span>
          <span>{latestFetch ? `Updated ${formatCapeTownDate(latestFetch)}` : "Awaiting first sync"}</span>
        </div>
      </div>

      <fieldset className="github-filters">
        <legend className="sr-only">Filter GitHub field notes</legend>
        <CustomSelect
          icon={<FunnelSimple aria-hidden="true" />}
          label="Field"
          options={CATEGORY_FILTER_OPTIONS}
          value={filters.category}
          onChange={(value) => updateFilter("category", value)}
        />
        <CustomSelect
          icon={<Star aria-hidden="true" />}
          label="Stars"
          options={STAR_FILTER_OPTIONS}
          value={filters.stars}
          onChange={(value) => updateFilter("stars", value)}
        />
        <CustomSelect
          icon={<Code aria-hidden="true" />}
          label="Tech"
          options={TECHNOLOGY_FILTER_OPTIONS}
          value={filters.technology}
          onChange={(value) => updateFilter("technology", value)}
        />
        <CustomSelect
          icon={<ArrowsDownUp aria-hidden="true" />}
          label="Sort"
          options={SORT_FILTER_OPTIONS}
          value={filters.sort}
          onChange={(value) => updateFilter("sort", value)}
        />
      </fieldset>

      <div className="github-showcase__summary" aria-live="polite">
        <span>{visibleEntries.length} of {entries.length}</span>
        <span>Stars show reach, not quality.</span>
      </div>

      {visibleEntries.length ? (
        // biome-ignore lint/a11y/noNoninteractiveTabindex: The overflow region must be keyboard-scrollable on desktop.
        <section className="github-showcase__viewport" aria-label="Curated GitHub repositories" tabIndex={0}>
          <div className="github-showcase__grid">
            {visibleEntries.map((entry) => <GithubProject key={entry.id} entry={entry} />)}
          </div>
        </section>
      ) : (
        <div className="github-showcase__empty" role="status">
          <strong>No repositories match these filters.</strong>
          <span>The saved set is still intact. Reset the filters to see all nine.</span>
          <button type="button" className="text-button" onClick={() => setFilters(DEFAULT_FILTERS)}>Reset filters</button>
        </div>
      )}
    </section>
  );
}
